import ProfitPostagePanel from "./ProfitPostagePanel";
import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, Stack, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { shippingApi } from "./api";
import SourceHealthPanel from "./SourceHealthPanel";
import OperationStatusPanel from "./OperationStatusPanel";
import DispatchPanel from "./DispatchPanel";
import PostageReconciliation from "./PostageReconciliation";

// Keep formatting local: no cyclic dependency on the dashboard.
const money = (fact) => fact?.value == null ? "Unknown" : `${fact.currency || "Currency unknown"} ${fact.value}`;
const stamp = (value) => value || "Not recorded";

function SectionCard({ title, action, children, sx }) {
  return <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, ...sx }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1.25 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>;
}

function packageColor(state) {
  if (["OUTGOING", "HANDED_OVER"].includes(state)) return "success";
  if (["PACKED", "SEALED", "CHECKED"].includes(state)) return "info";
  if (["HOLD", "VOID"].includes(state)) return "warning";
  return "default";
}

export default function OrderDetailPanel({ orderId, capabilities, onClose, onAccessLost, onPack }) {
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [events, setEvents] = useState(null);
  const [eventError, setEventError] = useState("");
  const [eventBusy, setEventBusy] = useState(false);
  const [eventBefore, setEventBefore] = useState(undefined);
  const [eventRevision, setEventRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError(""); setEvents(null); setEventBefore(undefined);
    shippingApi.detail(orderId, controller.signal).then((value) => {
      if (!controller.signal.aborted) setData(value);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || "Order detail could not be read.");
    });
    return () => controller.abort();
  }, [orderId, revision, onAccessLost]);

  useEffect(() => {
    if (eventBefore === undefined) return undefined;
    const controller = new AbortController();
    setEventBusy(true); setEventError("");
    shippingApi.events(orderId, eventBefore, controller.signal).then((value) => {
      if (!controller.signal.aborted) setEvents(value);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else { setEvents(null); setEventError(err.message || "Events could not be read."); }
    }).finally(() => { if (!controller.signal.aborted) setEventBusy(false); });
    return () => controller.abort();
  }, [orderId, eventBefore, eventRevision, onAccessLost]);

  const order = data?.order;
  const address = order?.shipping_address;
  const issues = order?.issues || [];

  return <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}>
    <DialogTitle sx={{ pb: 1.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
        <Box>
          <Typography variant="overline" color="text.secondary">Order overview</Typography>
          <Typography variant="h5" component="div" sx={{ fontWeight: 750, letterSpacing: "-0.01em" }}>{orderId}</Typography>
        </Box>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {order && <Chip size="small" color={order.candidate_for_shipping ? "primary" : "warning"} variant={order.candidate_for_shipping ? "outlined" : "filled"}
            label={order.candidate_for_shipping ? "Ready" : "Review required"} />}
          <Chip size="small" variant="outlined" label={capabilities?.packing?.enabled ? "Packing available" : "Packing disabled"} />
        </Stack>
      </Stack>
    </DialogTitle>
    <DialogContent dividers sx={{ overflowWrap: "anywhere", bgcolor: "grey.50", p: { xs: 1.5, sm: 2 } }}>
      {error ? <Alert severity="error" action={<Button onClick={() => setRevision((v) => v + 1)}>Retry</Button>}>{error}</Alert> : !data ? <CircularProgress aria-label="Reading order" /> : <Stack gap={2}>
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5}>
            <Box>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                <Chip size="small" variant="outlined" label={order.payment_status || "Payment unknown"} />
                <Chip size="small" variant="outlined" label={order.fulfillment_status || "Fulfilment unknown"} />
                <Chip size="small" variant="outlined" label={`Address · ${order.address_status || "Unknown"}`} />
              </Stack>
              <Typography variant="body2" color="text.secondary">Goods {money(order.money.goods_value)} · Buyer postage {money(order.money.shipping_paid)}</Typography>
            </Box>
            {capabilities?.packing?.enabled && <Button variant="contained" size="large" onClick={onPack} sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}>
              Start Packing
            </Button>}
          </Stack>
        </Paper>

        {issues.length > 0 && <SectionCard title="Needs attention">
          <Stack gap={1}>{issues.map((issue, i) => <Alert key={`${issue.code}-${i}`} severity={issue.severity === "HOLD" ? "warning" : "info"}>
            <strong>{issue.code}</strong> · {issue.message}{issue.field ? ` (${issue.field})` : ""}
          </Alert>)}</Stack>
        </SectionCard>}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 2 }}>
          <SectionCard title="Ship to">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Buyer account: {order.buyer_username || "Not recorded"}</Typography>
            {address ? <Box>{[address.full_name, address.address_line_1, address.address_line_2,
              address.city, address.state_or_province, address.postal_code, address.country_code].filter(Boolean).map((line, i) => <Typography key={i}>{line}</Typography>)}
              {address.reference_id && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>Routing reference: {address.reference_id}</Typography>}
            </Box> : <Alert severity="warning">No unambiguous supported shipping address is available.</Alert>}
          </SectionCard>

          <SectionCard title="Shipping facts">
            <Typography sx={{ fontWeight: 650 }}>Goods: {money(order.money.goods_value)}</Typography>
            <Typography>Buyer postage: {money(order.money.shipping_paid)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Checkout total: {money(order.money.checkout_total)}</Typography>
            <Typography variant="body2" color="text.secondary">Buyer-selected service: {order.buyer_selected_service || "Not established"}</Typography>
            <Typography variant="body2" color="text.secondary">Legacy imported service: {order.legacy_normalized_service || "Not recorded"}</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>Seller effective service is frozen per package in Packing Station.</Typography>
          </SectionCard>
        </Box>

        <SectionCard title={`Items · ${order.lines.length} sale line${order.lines.length === 1 ? "" : "s"}`}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Quantities are purchased sale units, not physical card counts.</Typography>
          <Stack gap={1}>{order.lines.map((line, index) => <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} key={`${line.local_item_pk}-${index}`}>
            <Typography sx={{ fontWeight: 650 }}>{line.ordered_sale_unit_quantity} × {line.title || "Untitled item"}</Typography>
            <Typography variant="body2">SKU: {line.sku || "Missing"} · Source line: {line.source_line_item_id || "Missing"}</Typography>
            <Typography variant="caption" color="text.secondary">Local row {line.local_item_pk ?? "Unknown"} · Native line {line.native_line_item_id || "Not verified"} · {line.source_line_identity_kind} · Transaction {line.transaction_id || "Not recorded"}</Typography>
          </Paper>)}</Stack>
        </SectionCard>

        <SectionCard title="Packages">
          {data.local.status !== "AVAILABLE" ? <Alert severity="warning">Local records unavailable: {data.local.status}. Package state is unknown.</Alert> : data.local.packages.length === 0 ? <Typography color="text.secondary">No local packages recorded yet.</Typography> : <Stack gap={1}>{data.local.packages.map((p) => <Paper key={p.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography sx={{ fontWeight: 700 }}>Package #{p.package_number}</Typography>
              <Chip size="small" color={packageColor(p.physical_state)} variant="outlined" label={p.physical_state} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.75 }}>Job: {p.job_id ? `${p.job_id} · ${p.job_status} · User ${p.assigned_user_id ?? "unassigned"}` : "None recorded"}</Typography>
            {p.items.map((item) => <Typography variant="body2" key={item.source_order_item_id}>Source row {item.source_order_item_id} / {item.source_line_item_id}: {item.allocated_sale_unit_quantity} sale units</Typography>)}
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>Package {p.package_key} · Manifest rev {p.manifest_revision} · Address rev {p.address_revision} · Row version {p.row_version}</Typography>
            {!fullScreen && ["OUTGOING", "HANDED_OVER"].includes(p.physical_state) && <Box sx={{ mt: 1.5 }}><DispatchPanel key={`${orderId}-${p.id}`} shipmentId={p.id} capabilities={capabilities} onAccessLost={onAccessLost} /></Box>}
          </Paper>)}</Stack>}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>Physical package state is separate from buyer-message and eBay-dispatch state.</Typography>
        </SectionCard>

        {(capabilities?.postage?.enabled || (!fullScreen && capabilities?.profit_postage?.enabled) || capabilities?.operations?.enabled) && <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 750, mb: 1 }}>Operational records</Typography>
          <Stack gap={2}>
            {capabilities?.postage?.enabled && <PostageReconciliation key={orderId} orderId={orderId} capabilities={capabilities} onAccessLost={onAccessLost} readOnly={fullScreen} />}
            {!fullScreen && capabilities?.profit_postage?.enabled && <ProfitPostagePanel key={`profit-${orderId}`} orderId={orderId} capabilities={capabilities} onAccessLost={onAccessLost} />}
            {capabilities?.operations?.enabled && <OperationStatusPanel key={orderId} orderId={orderId} onAccessLost={onAccessLost} capabilities={capabilities} />}
          </Stack>
        </Box>}

        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
          <Box component="details">
            <Box component="summary" sx={{ cursor: "pointer", p: 1.75, fontWeight: 750, userSelect: "none" }}>Source and audit detail</Box>
            <Divider />
            <Stack gap={2} sx={{ p: 2 }}>
              {capabilities?.source_health && <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Source safety</Typography>
                <SourceHealthPanel compact capabilities={capabilities} orderId={orderId} onAccessLost={onAccessLost} />
              </Box>}
              <Box>
                <Typography variant="subtitle2">Source freshness</Typography>
                <Typography variant="body2">Created: {stamp(order.created_at)}</Typography>
                <Typography variant="body2">Marketplace modified: {stamp(order.marketplace_last_modified)}</Typography>
                <Typography variant="body2">Local row updated: {stamp(order.local_import_updated_at)}</Typography>
                <Typography variant="caption" color="text.secondary">These timestamps do not establish when eBay was last checked.</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Cancellation and refund evidence</Typography>
                <Typography variant="body2">Cancellation: {order.cancellation_state || "Unknown"} · Observed requests: {order.cancellation_request_count} · Refund activity: {order.has_refund_activity ? "Yes" : "None observed"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Projection issues</Typography>
                {issues.length === 0 ? <Typography variant="body2">No projection issues. This does not authorise packing.</Typography> : <Typography variant="body2">{issues.length} issue{issues.length === 1 ? "" : "s"} shown in Needs attention above.</Typography>}
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2">Local event history</Typography>
                <Typography variant="body2" color="text.secondary">Event metadata only; addresses, command payloads and lease credentials are excluded.</Typography>
                {eventError && <Alert severity="error" sx={{ mt: 1 }}>{eventError}</Alert>}
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 1 }}>
                  <Button size="small" variant="outlined" disabled={eventBusy} onClick={() => { setEventBefore(undefined); setRevision((v) => v + 1); }}>Reload detail</Button>
                  <Button size="small" variant="outlined" disabled={eventBusy || (events && !events.next_before_id)} onClick={() => { setEventBefore(events?.next_before_id ?? null); setEventRevision((v) => v + 1); }}>
                    {eventBusy ? "Reading…" : eventError ? "Retry events" : events ? "Older events" : "Read events"}
                  </Button>
                </Stack>
                {events && (events.status !== "AVAILABLE" ? <Alert severity="warning">Event history unavailable: {events.status}</Alert> : events.events.length === 0 ? <Typography>No events recorded.</Typography> : events.events.map((event) =>
                  <Typography key={event.id} variant="body2">{stamp(event.occurred_at)} · {event.event_type} · Package {event.shipment_id ?? "—"} · {event.actor_kind}{event.actor_user_id ? ` ${event.actor_user_id}` : ""}</Typography>))}
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Stack>}
    </DialogContent>
    <DialogActions sx={{ px: 2, py: 1.25 }}><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
